import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Button, Box, IconButton, Tooltip } from '@mui/material';
import { Add, ArrowDropDown, ArrowDropUp, InfoOutlined, SimCardDownload } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { formatDate } from 'src/shared/functions/formatDate';
import { RootState } from 'src/shared/services/store';
import { useTranslation } from 'react-i18next';
import { ContentSection } from 'src/shared/components/organisms/contentSection';
import { useCallback, useState } from 'react';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { UserAccountTypeEnum } from 'src/shared/models/enums/userAccountType.enum';
import { useGlobals } from 'src/shared/contexts/appContext';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { Link } from 'react-router-dom';
import { downloadInvoicePdf } from 'src/shared/functions/downloadInvoicePdf';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { Colors } from 'src/shared/components/styles/colors';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { Invoice } from 'src/modules/invoice/models/invoice';
import { MemberCreateInvoice } from './memberCreateInvoice';
import { Member } from '../models/member';

type MemberInvoicesProps = {
  otherContactMode: boolean;
  member: Member;
};

const MemberInvoices = (props: MemberInvoicesProps) => {
  const { otherContactMode, member } = props;
  const { t } = useTranslation(['invoice']);
  const { setDateStamp, currentUser } = useGlobals();
  const user = useSelector((state: RootState) => state.oidc.user);
  const isMember = user && user.profile.accountType === UserAccountTypeEnum.Member;

  const [showMore, setShowMore] = useState<boolean>(false);
  const [showCreateInvoiceModal, setShowCreateInvoiceModal] = useState<boolean>(false);
  const [loadingPDF, setLoadingPDF] = useState<boolean>(false);

  const emptyCell = <Typography sx={{ color: (thm) => thm.palette.grey[300] }}> &mdash;</Typography>;
  const hasMemberUpdatePermission = getPermission(currentUser, PermissionEnum.MemberUpdate);
  const hasInvoiceaccess = getPermission(currentUser, PermissionEnum.InvoiceRead);

  // Dont show the invoice link for users of type Member or user missing access
  const InvoiceLink = ({ linkURL, ocr }) => {
    return isMember || !hasInvoiceaccess ? <>{ocr}</> : <Link to={linkURL}>{ocr}</Link>;
  };

  const pdfLoadingCallback = useCallback((isLoading: boolean) => {
    setLoadingPDF(isLoading);
  }, []);

  const downloadPdf = async (id) => {
    downloadInvoicePdf(id, pdfLoadingCallback);
  };

  // Tillgodo räknas i API:et och finns bara för fakturagrupper med konto för överbetalning.
  const credits = member.credits ?? [];
  const creditInvoiceIds = credits.flatMap((credit) => credit.invoiceIds);

  const renderOverpaid = (invoice: Invoice) => {
    if (!creditInvoiceIds.includes(invoice.invoice_id)) return null;
    return (
      <Typography variant='caption' color='text.secondary' component='div'>
        {t('invoice:memberCredit.overpaid')} <AmountWithCurrency amount={invoice.paid - (invoice.amount ?? 0)} />
      </Typography>
    );
  };

  return (
    <>
      <LoadingOverlay message={t('invoice:loadingPdf')} loading={loadingPDF} />
      {credits.length > 0 && (
        <ContentSection header={t('invoice:memberCredit.header')} mb={2}>
          {credits.map((credit) => (
            <Box key={credit.ownerId} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, py: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant='body2'>{credit.ownerName}</Typography>
                <Tooltip title={t('invoice:memberCredit.info', { account: credit.excessAccount }) as string}>
                  <InfoOutlined fontSize='small' color='action' />
                </Tooltip>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant='body2' sx={{ fontWeight: 700 }}>
                  <AmountWithCurrency amount={credit.amount} />
                </Typography>
                {/* Har en del av tillgodot redan dragits av på en senare faktura är beloppet
                    ovanför mindre än överbetalningen. Utan den här raden ser det ut som att
                    pengar försvunnit. */}
                {credit.usedAmount > 0 && (
                  <Typography variant='caption' color='text.secondary'>
                    {t('invoice:memberCredit.used', { used: credit.usedAmount, overpaid: credit.overpaidAmount })}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </ContentSection>
      )}
      <ContentSection
        header={t('invoice:unpaidInvoiceheader')}
        showHeaderCounter
        headerCounter={(member.currentInvoices && member.currentInvoices?.length) ?? 0}
        showAlertBadge
        button={
          // Create invoice is disabled for users of type Member or in otherContactMode
          isMember || otherContactMode ? undefined : (
            <Button
              disabled={!hasMemberUpdatePermission}
              size='small'
              variant='outlined'
              endIcon={<Add fontSize='small' />}
              onClick={() => {
                setShowCreateInvoiceModal(true);
              }}
            >
              {t('common:button.create')}
            </Button>
          )
        }
      >
        {/* Current Invoices */}
        {member.currentInvoices && member.currentInvoices.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('invoice:OCR')}</TableCell>
                  <TableCell align='right'>{t('invoice:amount')}</TableCell>
                  <TableCell>{t('invoice:dueDate')}</TableCell>
                  <TableCell align='right'>{t('invoice:paid')}</TableCell>
                  <TableCell>{t('invoice:paidDate')}</TableCell>
                  <TableCell>{t('invoice:payOnline')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {member.currentInvoices.map((invoice) => {
                  const FSPayLink = `${process.env.REACT_APP_FS_PAY_URL}/${invoice.invoice_id}/${invoice.invoiceGuid}/${user?.profile.custname}`;
                  return (
                    <TableRow key={invoice.invoice_id}>
                      <TableCell>
                        <InvoiceLink linkURL={`${RoutesEnum.InvoicesDetail}/${invoice.invoice_id}`} ocr={invoice.ocr} />
                      </TableCell>
                      <TableCell align='right'>
                        <AmountWithCurrency amount={invoice.amount} />
                      </TableCell>
                      <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                      <TableCell align='right'>
                        <AmountWithCurrency amount={invoice.paid} />
                      </TableCell>
                      <TableCell>{formatDate(invoice.paidDate) || emptyCell}</TableCell>
                      <TableCell>
                        <Button type='button' variant='text' size='small' href={FSPayLink} target='_blank'>
                          {t('invoice:openInFSPay')}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={t('invoice:DownloadAsPDF')}>
                          <span>
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadPdf(invoice.invoice_id);
                              }}
                              style={{ display: 'flex', color: Colors.LinkColor }}
                              size='small'
                            >
                              <SimCardDownload />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography sx={{ color: 'grey.500', py: 1 }}>{t('invoice:noUnpaidInvoices')}</Typography>
        )}
      </ContentSection>

      {/* Last Paid Invoices */}
      {member.lastPaidInvoice && (
        <Box sx={{ mt: 2 }}>
          <ContentSection
            header={member.paidInvoices && member.paidInvoices.length > 1 ? t('invoice:paidInvoicesHeader') : t('invoice:paidInvoiceHeader')}
          >
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('invoice:OCR')}</TableCell>
                    <TableCell align='right'>{t('invoice:amount')}</TableCell>
                    <TableCell>{t('invoice:dueDate')}</TableCell>
                    <TableCell align='right'>{t('invoice:paid')}</TableCell>
                    <TableCell>{t('invoice:paidDate')}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {member.lastPaidInvoice && (
                    <TableRow key={member.lastPaidInvoice.invoice_id}>
                      <TableCell>
                        <InvoiceLink linkURL={`${RoutesEnum.InvoicesDetail}/${member.lastPaidInvoice.invoice_id}`} ocr={member.lastPaidInvoice.ocr} />
                      </TableCell>
                      <TableCell align='right'>
                        <AmountWithCurrency amount={member.lastPaidInvoice.amount} />
                      </TableCell>
                      <TableCell>{formatDate(member.lastPaidInvoice.dueDate)}</TableCell>
                      <TableCell align='right'>
                        <AmountWithCurrency amount={member.lastPaidInvoice.paid} />
                        {renderOverpaid(member.lastPaidInvoice)}
                      </TableCell>
                      <TableCell>{formatDate(member.lastPaidInvoice.paidDate)}</TableCell>
                      <TableCell sx={{ justifyContent: 'center', display: 'flex' }}>
                        <Tooltip title={t('invoice:DownloadAsPDF')}>
                          <span>
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadPdf(member.lastPaidInvoice?.invoice_id);
                              }}
                              style={{ display: 'flex', color: Colors.LinkColor }}
                              size='small'
                            >
                              <SimCardDownload />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  )}
                  {/* Showmore Paid Invoices */}
                  {showMore &&
                    member.paidInvoices &&
                    member.paidInvoices.length > 1 &&
                    member.paidInvoices
                      .filter((invoice) => invoice.invoice_id !== member.lastPaidInvoice?.invoice_id)
                      .map((invoice) => (
                        <TableRow key={invoice.invoice_id}>
                          <TableCell>
                            <InvoiceLink linkURL={`${RoutesEnum.InvoicesDetail}/${invoice.invoice_id}`} ocr={invoice.ocr} />
                          </TableCell>
                          <TableCell align='right'>
                            <AmountWithCurrency amount={invoice.amount} />
                          </TableCell>
                          <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                          <TableCell align='right'>
                            <AmountWithCurrency amount={invoice.paid} />
                            {renderOverpaid(invoice)}
                          </TableCell>
                          <TableCell>{formatDate(invoice.paidDate)}</TableCell>
                          <TableCell sx={{ justifyContent: 'center', display: 'flex' }}>
                            <Tooltip title={t('invoice:DownloadAsPDF')}>
                              <span>
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    downloadPdf(invoice.invoice_id);
                                  }}
                                  style={{ display: 'flex', color: Colors.LinkColor }}
                                  size='small'
                                >
                                  <SimCardDownload />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                </TableBody>
              </Table>
              {member.paidInvoices && member.paidInvoices.length > 1 && (
                <Box>
                  <Button
                    sx={{ pt: 2 }}
                    variant='text'
                    size='small'
                    onClick={() => setShowMore(!showMore)}
                    endIcon={showMore ? <ArrowDropUp /> : <ArrowDropDown />}
                  >
                    {showMore ? t('common:button.hide') : t('common:button.moreItems')}
                  </Button>
                </Box>
              )}
            </TableContainer>
          </ContentSection>
        </Box>
      )}
      <MemberCreateInvoice
        otherContactMode={otherContactMode}
        member={member}
        modalOpen={showCreateInvoiceModal}
        onClose={() => setShowCreateInvoiceModal(false)}
        onSave={() => {
          setDateStamp(new Date());
          setShowCreateInvoiceModal(false);
        }}
      />
    </>
  );
};

export default MemberInvoices;
